import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MessageSquare, GitBranch, ArrowRight, Radio, ShieldQuestion } from "lucide-react";

export default function ScenarioList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/scenarios").then(r => setItems(r.data)); }, []);

  return (
    <div className="space-y-10">
      <div>
        <div className="label-eyebrow mb-3">Role-Play Studio</div>
        <h1 className="font-display text-5xl md:text-6xl leading-[1.02]">Practice like it&rsquo;s real. <em className="italic">Because it will be.</em></h1>
        <p className="mt-4 text-neutral-500 max-w-2xl">Live AI conversations and branching drills, scored against real coaching rubrics. Come back as often as you like.</p>
      </div>

      <Link to="/simulator" data-testid="simulator-card"
            className="block border-2 border-[color:var(--brand-primary)] bg-[color:var(--brand-primary)] text-white p-8 md:p-10 group">
        <div className="flex items-center justify-between">
          <Radio size={20} strokeWidth={1.25} />
          <span className="label-eyebrow text-white/60">Build your own prospect</span>
        </div>
        <div className="mt-6 font-display text-3xl md:text-4xl leading-tight">The Practice Simulator</div>
        <p className="mt-2 text-sm text-white/70 max-w-2xl">Choose any combination of prospect type, personality style, blocker and difficulty for your training track. Stage tracker, live hints, mid-call coaching and a full AI debrief.</p>
        <span className="mt-6 inline-flex items-center gap-1 text-sm link-underline">Open the simulator <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
      </Link>

      <Link to="/obstacle-trainer" data-testid="obstacle-trainer-card"
            className="block card-hairline border-2 p-8 md:p-10 group hover:border-[color:var(--brand-primary)] transition-colors">
        <div className="flex items-center justify-between">
          <ShieldQuestion size={20} strokeWidth={1.25} className="text-[var(--brand-primary)]" />
          <span className="label-eyebrow">Objection drills · step-by-step</span>
        </div>
        <div className="mt-6 font-display text-3xl md:text-4xl leading-tight">The Obstacle Trainer</div>
        <p className="mt-2 text-sm text-neutral-500 max-w-2xl">Drill every obstacle from your track's field guide. Say your response out loud, then reveal the word-for-word structured answer — one step at a time.</p>
        <span className="mt-6 inline-flex items-center gap-1 text-sm link-underline">Start drilling <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
      </Link>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(s => (
          <Link
            key={s.id}
            to={s.kind === "ai_chat" ? `/roleplay/${s.id}` : `/branch/${s.id}`}
            data-testid={`sc-${s.id}`}
            className="card-hairline p-7 flex flex-col"
          >
            <div className="flex items-center justify-between">
              {s.kind === "ai_chat"
                ? <MessageSquare size={18} strokeWidth={1.25} className="text-[var(--brand-primary)]"/>
                : <GitBranch size={18} strokeWidth={1.25} className="text-[var(--brand-primary)]"/>}
              <div className="label-eyebrow text-neutral-400">{s.stage}</div>
            </div>
            <div className="mt-8 font-display text-2xl leading-tight">{s.title}</div>
            <div className="mt-2 text-sm text-neutral-500 flex-1">{s.description}</div>
            <div className="mt-6 flex items-center justify-between text-sm">
              <span className="label-eyebrow text-[var(--brand-primary)]">{s.difficulty}</span>
              <span className="flex items-center gap-1 text-[var(--brand-primary)] link-underline">
                {s.kind === "ai_chat" ? "Start conversation" : "Begin drill"} <ArrowRight size={14}/>
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
